import React, { Component, Fragment } from 'react';
import { func, object, string } from 'prop-types';
import classNames from 'classnames';
import { intlShape } from '../../util/reactIntl';
import config from '../../config';
import {
  dateIsAfter,
  getEndHours,
  getMonthStartInTimeZone,
  getStartHours,
  isDayMomentInsideRange,
  isInRange,
  isSameDate,
  monthIdStringInTimeZone,
  nextMonthFn,
  prevMonthFn,
  resetToStartOfDay,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  timestampToDate,
} from '../../util/dates';
import { propTypes } from '../../util/types';
import { bookingDateRequired } from '../../util/validators';
import { FieldDateInput, FieldSelect } from '../../components';
import moment from 'moment';

import NextMonthIcon from './NextMonthIcon';
import PreviousMonthIcon from './PreviousMonthIcon';
import css from './FieldDateAndTimeInput.css';
import { uniqBy } from 'lodash';

const MAX_TIME_SLOTS_RANGE = 360;
const TODAY = new Date();
const sixMonthFromNow = new Date(new Date().setMonth(new Date().getMonth() + 5)).setHours(0, 0, 0, 0);
let availableEndTimes = [];
const endOfRange = (date, timeZone) => {
  return resetToStartOfDay(date, timeZone, MAX_TIME_SLOTS_RANGE - 1);
};

export const getAvailableStartTimes = (
  intl,
  timeZone,
  bookingStart,
  timeSlotsOnSelectedDate,
  localTimeZone
) => {
  if (timeSlotsOnSelectedDate.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }
  const bookingStartDate = resetToStartOfDay(bookingStart, localTimeZone);

  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;

    const nextDate = resetToStartOfDay(bookingStartDate, localTimeZone, 1);

    // If the start date is after timeslot start, use the start date.
    // Otherwise use the timeslot start time.
    let startLimit = dateIsAfter(bookingStartDate, startDate) ? bookingStartDate : startDate;
    startLimit = new Date().getTime() > startLimit.getTime() ? new Date() : startLimit;

    // If date next to selected start date is inside timeslot use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = dateIsAfter(endDate, nextDate) ? nextDate : endDate;

    const hours = getStartHours(intl, localTimeZone, startLimit, endLimit);
    return availableHours.concat(hours);
  }, []);
  return uniqBy(allHours, i => i.timestamp);
};

const getAvailableStartTimesForUpdateBooking = (
  intl,
  timeZone,
  bookingStartTime,
  bookingStart,
  timeSlotsOnSelectedDate,
  localTimeZone
) => {
  if (timeSlotsOnSelectedDate.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }


  const bookingStartDate = resetToStartOfDay(bookingStart, localTimeZone);
  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;

    const nextDate = resetToStartOfDay(bookingStartDate, localTimeZone, 1);

    // If the start date is after timeslot start, use the start date.
    // Otherwise use the timeslot start time.
    let startLimit = dateIsAfter(bookingStartDate, startDate) ? bookingStartDate : startDate;
    startLimit = new Date().getTime() > startLimit.getTime() ? new Date() : startLimit;

    // If date next to selected start date is inside timeslot use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = dateIsAfter(endDate, nextDate) ? nextDate : endDate;
    const hours = getStartHours(intl, localTimeZone, startLimit, endLimit);
    return availableHours.concat(hours);
  }, []);

  return uniqBy(allHours, i => i.timestamp);
};

export const getEditListingAvailableStartTimes = (
  intl,
  bookingStart,
  bookingEnd,
  localTimeZone
) => {
  const startDate = bookingStart;
  const endDate = bookingEnd;
  const hours = getStartHours(intl, localTimeZone, startDate, endDate, false);
  return uniqBy(hours, i => i.timestamp);
};

export const getEditListingAvailableEndTimes = (
  intl,
  timeZone,
  bookingStartTime,
  bookingEndDate,
  selectedTimeSlot,
  localTimeZone
) => {
  if (!selectedTimeSlot || !selectedTimeSlot.attributes || !bookingEndDate || !bookingStartTime) {
    return [];
  }

  const endDate = selectedTimeSlot.attributes.end;
  const bookingStartTimeAsDate = timestampToDate(bookingStartTime);
  const dayAfterBookingEnd = resetToStartOfDay(bookingEndDate, localTimeZone, 1);
  const dayAfterBookingStart = resetToStartOfDay(bookingStartTimeAsDate, localTimeZone, 1);
  const startOfEndDay = resetToStartOfDay(bookingEndDate, localTimeZone);
  let startLimit;
  let endLimit;
  if (!dateIsAfter(startOfEndDay, bookingStartTimeAsDate)) {
    startLimit = bookingStartTimeAsDate;
    endLimit = dateIsAfter(dayAfterBookingStart, endDate) ? endDate : dayAfterBookingStart;
  } else {
    // If the end date is on the same day as the selected booking start time
    // use the start time as limit. Otherwise use the start of the selected end date.
    startLimit = dateIsAfter(bookingStartTimeAsDate, startOfEndDay)
      ? bookingStartTimeAsDate
      : startOfEndDay;

    // If the selected end date is on the same day as timeslot end, use the timeslot end.
    // Else use the start of the next day after selected date.
    endLimit = isSameDate(resetToStartOfDay(endDate, localTimeZone), startOfEndDay)
      ? endDate
      : dayAfterBookingEnd;
  }

  return uniqBy(getEndHours(intl, localTimeZone, startLimit, endLimit), i => i.timestamp);
};

export const getAvailableEndTimes = (
  intl,
  timeZone,
  bookingStartTime,
  bookingEndDate,
  selectedTimeSlot,
  localTimeZone
) => {
  if (!selectedTimeSlot || !selectedTimeSlot.attributes || !bookingEndDate || !bookingStartTime) {
    return [];
  }

  const endDate = selectedTimeSlot.attributes.end;
  const bookingStartTimeAsDate = timestampToDate(bookingStartTime);
  const dayAfterBookingEnd = resetToStartOfDay(bookingEndDate, localTimeZone, 1);
  const dayAfterBookingStart = resetToStartOfDay(bookingStartTimeAsDate, localTimeZone, 1);
  const startOfEndDay = resetToStartOfDay(bookingEndDate, localTimeZone);
  let startLimit;
  let endLimit;
  if (!dateIsAfter(startOfEndDay, bookingStartTimeAsDate)) {
    startLimit = bookingStartTimeAsDate;
    endLimit = dateIsAfter(dayAfterBookingStart, endDate) ? endDate : dayAfterBookingStart;
  } else {
    // If the end date is on the same day as the selected booking start time
    // use the start time as limit. Otherwise use the start of the selected end date.
    startLimit = dateIsAfter(bookingStartTimeAsDate, startOfEndDay)
      ? bookingStartTimeAsDate
      : startOfEndDay;

    // If the selected end date is on the same day as timeslot end, use the timeslot end.
    // Else use the start of the next day after selected date.
    endLimit = isSameDate(resetToStartOfDay(endDate, localTimeZone), startOfEndDay)
      ? endDate
      : dayAfterBookingEnd;
  }
  return uniqBy(getEndHours(intl, localTimeZone, startLimit, endLimit), i => i.timestamp);
};

const getAvailableEndTimesForUpdateBooking = (
  intl,
  timeZone,
  bookingEndTime,
  bookingStartTime,
  bookingEndDate,
  bookingStart,
  timeSlotsOnSelectedDate,
  localTimeZone
) => {
  if (timeSlotsOnSelectedDate.length === 0 || !timeSlotsOnSelectedDate[0] || !bookingStart) {
    return [];
  }
  const bookingStartDate = resetToStartOfDay(bookingEndDate, localTimeZone);
  const bookingStartTimeAsDate = timestampToDate(bookingEndTime);
  const startOfEndDay = resetToStartOfDay(bookingEndDate, localTimeZone);


  const allHours = timeSlotsOnSelectedDate.reduce((availableHours, t) => {
    const startDate = t.attributes.start;
    const endDate = t.attributes.end;
    const nextDate = resetToStartOfDay(bookingStartDate, localTimeZone, 1);

    let startLimit;
    if (!dateIsAfter(startOfEndDay, bookingStartTimeAsDate)) {
      startLimit = bookingStartTimeAsDate;
    } else {
      // If the end date is on the same day as the selected booking start time
      // use the start time as limit. Otherwise use the start of the selected end date.
      startLimit = dateIsAfter(bookingStartTimeAsDate, startOfEndDay)
        ? bookingStartTimeAsDate
        : startOfEndDay;
    }

    // If date next to selected start date is inside timeslot use the next date to get the hours of full day.
    // Otherwise use the end of the timeslot.
    const endLimit = dateIsAfter(endDate, nextDate) ? nextDate : endDate;
    const hours = getStartHours(intl, localTimeZone, startLimit, endLimit);
    return availableHours.concat(hours);
  }, []);

  return uniqBy(allHours, i => i.timestamp);
};


const getTimeSlots = (timeSlots, date, timeZone) => {
return timeSlots && timeSlots[0]
    ? timeSlots.filter(t => isInRange(date, t.attributes.start, t.attributes.end, 'day', timeZone))
    : [];
};

export const getAllSelectedTimeSlots = (timeSlots, firstSelectedSlot) => {
  if (!firstSelectedSlot) {
    return [];
  }
  let selectedSlots = [firstSelectedSlot];
  let currentEndSlot = moment(firstSelectedSlot.attributes.end)
    .add(1, 'millisecond')
    .toDate();

  let slot = undefined;
  do {
    slot = timeSlots.find(t => isInRange(currentEndSlot, t.attributes.start, t.attributes.end));
    if (slot) {
      selectedSlots = selectedSlots.concat(slot);
      currentEndSlot = moment(slot.attributes.end).add(1, 'millisecond');
    }
  } while (slot);

  return selectedSlots;
};

// Use start date to calculate the first possible start time or times, end date and end time or times.
// If the selected value is passed to function it will be used instead of calculated value.
const getAllTimeValues = (
  intl,
  timeZone,
  timeSlotsOnSelectedDate,
  startDate,
  localTimeZone,
  selectedStartTime,
  selectedEndDate,
  timeSlots
) => {

  const startTimes = selectedStartTime
    ? []
    : getAvailableStartTimes(
      intl,
      timeZone,
      startDate,
      getTimeSlots(timeSlotsOnSelectedDate, startDate, timeZone)
    );

  const startTime = selectedStartTime
    ? selectedStartTime
    : startTimes.length > 0 && startTimes[0] && startTimes[0].timestamp
      ? startTimes[0].timestamp
      : null;

  const startTimeAsDate = startTime ? timestampToDate(startTime) : null;

  // Note: We need to remove 1ms from the calculated endDate so that if the end
  // date would be the next day at 00:00 the day in the form is still correct.
  // Because we are only using the date and not the exact time we can remove the
  // 1ms.
  const endDate = selectedEndDate ? selectedEndDate : null;

  const selectedTimeSlot = timeSlotsOnSelectedDate.find(t =>
    isInRange(startTimeAsDate, t.attributes.start, t.attributes.end)
  );

  const selectedTimeSlots = getAllSelectedTimeSlots(timeSlots, selectedTimeSlot);
  const endTimes = getAvailableEndTimes(
    intl,
    timeZone,
    startTime,
    endDate,
    selectedTimeSlot,
    localTimeZone
  );
  const endTime =
    endTimes.length > 0 && endTimes[0] && endTimes[0].timestamp ? endTimes[0].timestamp : null;
  return { startTime, endDate, endTime, selectedTimeSlot, selectedTimeSlots };
};

const getMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdStringInTimeZone(date, timeZone);


  return !monthlyTimeSlots || Object.keys(monthlyTimeSlots).length === 0
    ? []
    : monthlyTimeSlots[monthId] && monthlyTimeSlots[monthId].timeSlots
      ? monthlyTimeSlots[monthId].timeSlots
      : [];
};

const Next = props => {
  const { currentMonth, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return dateIsAfter(nextMonthDate, endOfRange(TODAY, timeZone)) ? null : <NextMonthIcon />;
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getMonthStartInTimeZone(TODAY, timeZone);

  return dateIsAfter(prevMonthDate, currentMonthDate) ? <PreviousMonthIcon /> : null;
};

/////////////////////////////////////
// FieldDateAndTimeInput component //
/////////////////////////////////////
class FieldDateAndTimeInput extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentMonth: getMonthStartInTimeZone(TODAY, props.localTimeZone),
      ifDropOffChanged: true,
    };

    this.fetchMonthData = this.fetchMonthData.bind(this);
    this.onMonthClick = this.onMonthClick.bind(this);
    this.onBookingStartDateChange = this.onBookingStartDateChange.bind(this);
    this.onBookingStartTimeChange = this.onBookingStartTimeChange.bind(this);
    this.onBookingEndDateChange = this.onBookingEndDateChange.bind(this);
    this.isOutsideRange = this.isOutsideRange.bind(this);
  }

  componentDidMount() {
    const { initialValues, localTimeZone, updateBooking } = this.props;
    if(updateBooking) {
      this.setState({ currentMonth: getMonthStartInTimeZone(initialValues.bookingEndDate.date, localTimeZone) })
      this.fetchMonthData(new Date(initialValues.bookingStartDate.date.setHours(0, 0, 0, 0)));
      const dummyStartDate = new Date(moment(initialValues.bookingStartDate.date).add(9, 'months'));
      this.fetchMonthData(new Date(dummyStartDate));
    }
  }

  fetchMonthData(date) {
    const { listingId, timeZone, onFetchTimeSlots } = this.props;
    const endOfRangeDate = endOfRange(TODAY, timeZone);

    // Don't fetch timeSlots for past months or too far in the future
    if (isInRange(date, TODAY, endOfRangeDate)) {
      // Use "today", if the first day of given month is in the past
      const start = dateIsAfter(TODAY, date) ? TODAY : date;

      // Use endOfRangeDate, if the first day of the next month is too far in the future
      const nextMonthDate = nextMonthFn(date, timeZone);
      const end = dateIsAfter(nextMonthDate, endOfRangeDate)
        ? resetToStartOfDay(endOfRangeDate, timeZone, 0)
        : nextMonthDate;

      // Fetch time slots for given time range
      onFetchTimeSlots(listingId, start, end, timeZone);
    }
  }

  onMonthClick(monthFn) {
    const { timeZone } = this.props;
    this.setState(prevState => ({ currentMonth: monthFn(prevState.currentMonth, timeZone) }));
  }

  onBookingStartDateChange = value => {
    const { monthlyTimeSlots, timeZone, intl, form, localTimeZone } = this.props;
    if (!value || !value.date) {
      form.batch(() => {
        form.change('bookingStartTime', null);
        // form.change('bookingEndDate', null);
        // form.change('bookingEndTime', null);
      });
      // Reset the currentMonth too if bookingStartDate is cleared
      this.setState({ currentMonth: getMonthStartInTimeZone(TODAY, timeZone) });

      return;
    }

    // This callback function (onBookingStartDateChange) is called from react-dates component.
    // It gets raw value as a param - browser's local time instead of time in listing's timezone.
    const startDate = timeOfDayFromLocalToTimeZone(value.date, timeZone);
    const timeSlots = getMonthlyTimeSlots(monthlyTimeSlots, this.state.currentMonth, timeZone);
    const timeSlotsOnSelectedDate = getTimeSlots(timeSlots, startDate, timeZone);

    const { startTime, endDate, endTime } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      startDate,
      localTimeZone
    );

    form.batch(() => {
      form.change('bookingStartTime', null);
      // form.change('bookingEndDate', null);
      // form.change('bookingEndTime', null);
    });
    this.setState({
      ifDropOffChanged: false,
      currentMonth: getMonthStartInTimeZone(startDate, localTimeZone)
    });
  };

  onBookingStartTimeChange = value => {
    const { form } = this.props;

    form.batch(() => {
      // form.change('bookingEndDate', null);
      // form.change('bookingEndTime', null);
    });
  };

  onBookingEndDateChange = value => {
    const { form, values } = this.props;
    const prevDropOff =
      values.bookingEndDate && values.bookingEndDate.date ? values.bookingEndDate.date : null;
    const newDropOff = value && value.date;
    const isSameDate = moment(newDropOff).isSame(prevDropOff, 'day');
    if (!isSameDate) {
      this.setState({
        ifDropOffChanged: true,
      });
    } else {
      this.setState({
        ifDropOffChanged: false,
      });
    }
    if (!value || !value.date) {
      // form.change('bookingEndTime', null);
      return;
    }

    // form.change('bookingEndTime', null);
  };

  isOutsideRange(day, bookingStartDate, selectedTimeSlot, timeZone) {
    if (!selectedTimeSlot) {
      return true;
    }

    // 'day' is pointing to browser's local time-zone (react-dates gives these).
    // However, bookingStartDate and selectedTimeSlot refer to times in listing's timeZone.
    const localizedDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    // Given day (endDate) should be after the start of the day of selected booking start date.
    const startDate = resetToStartOfDay(bookingStartDate, timeZone);
    // 00:00 would return wrong day as the end date.
    // Removing 1 millisecond, solves the exclusivity issue.
    const inclusiveEnd = new Date(selectedTimeSlot.attributes.end.getTime() - 1);
    // Given day (endDate) should be before the "next" day of selected timeSlots end.
    const endDate = resetToStartOfDay(inclusiveEnd, timeZone, 1);
    return (
      !(dateIsAfter(localizedDay, startDate) && dateIsAfter(endDate, localizedDay)) ||
      this.props.isOutsideRange(localizedDay)
    );
  }

  startDateIsOutsizeRange = (day, timeZone) => {
    const localizedDay = timeOfDayFromLocalToTimeZone(day, timeZone);

    return this.props.isOutsideRange(localizedDay);
  };

  render() {
    const {
      rootClassName,
      className,
      formId,
      startDateInputProps,
      endDateInputProps,
      values,
      timeZone,
      intl,
      timeSlots: monthlyTimeSlots,
      localTimeZone,
      updateBooking,
      initialValues,
    } = this.props;
    const { ifDropOffChanged } = this.state;
    const classes = classNames(rootClassName || css.root, className);
    const bookingStartDate =
      values.bookingStartDate && values.bookingStartDate.date ? values.bookingStartDate.date : null;
    const bookingStartTime = values.bookingStartTime ? values.bookingStartTime : null;
    const bookingEndTime = values.bookingEndTime ? values.bookingEndTime : null;
    const bookingEndDate =
      values.bookingEndDate && values.bookingEndDate.date ? values.bookingEndDate.date : null;

    const timeSlotsOnSelectedMonth = monthlyTimeSlots;
    const timeSlotsOnSelectedDate = getTimeSlots(
      timeSlotsOnSelectedMonth,
      bookingStartDate,
      localTimeZone
    );

    let availableStartTimes = getAvailableStartTimes(
      intl,
      timeZone,
      bookingStartDate,
      timeSlotsOnSelectedDate,
      localTimeZone
    );

    let availableStartTimesForUpdateBooking = getAvailableStartTimesForUpdateBooking(
      intl,
      timeZone,
      bookingStartTime,
      bookingStartDate,
      timeSlotsOnSelectedDate,
      localTimeZone
    );

    availableStartTimes = updateBooking ? [...availableStartTimesForUpdateBooking, ...config.custom.times] : [...availableStartTimes, ...config.custom.times];
    const filteredStartTimes = [];
    const filteredAvailableStartTimes = availableStartTimes.filter(a => {
      if (filteredStartTimes.indexOf(a.timeOfDay) === -1) {
        filteredStartTimes.push(a.timeOfDay);
        return a;
      }
    });

    filteredAvailableStartTimes.sort( (a, b) => Date.parse('01/01/2013 ' + a.timeOfDay) - Date.parse('01/01/2013 ' + b.timeOfDay));
    const startOptions = bookingStartDate
      ? filteredAvailableStartTimes.map(p => {
        const optionClass = p.className === 'unavailable' ? css.unavailable : css.available;
        const disabled = p.className === 'unavailable';
        return { value: p.timestamp, label: p.timeOfDay, className: optionClass, disabled };
      })
      : [];

    const firstAvailableStartTime =
      availableStartTimes.length > 0 && availableStartTimes[0] && availableStartTimes[0].timestamp
        ? availableStartTimes[0].timestamp
        : null;

    const { startTime, endDate, selectedTimeSlot, selectedTimeSlots } = getAllTimeValues(
      intl,
      timeZone,
      timeSlotsOnSelectedDate,
      bookingStartDate,
      localTimeZone,
      bookingStartTime || firstAvailableStartTime,
      bookingEndDate || bookingStartDate,
      monthlyTimeSlots
    );
    availableEndTimes = getAvailableEndTimes(
      intl,
      timeZone,
      bookingStartTime,
      bookingEndDate,
      selectedTimeSlot,
      localTimeZone
    );

    let availableEndTimesForUpdateBooking = getAvailableEndTimesForUpdateBooking(
      intl,
      timeZone,
      bookingEndTime,
      bookingStartTime,
      bookingEndDate,
      bookingStartDate,
      timeSlotsOnSelectedDate,
      localTimeZone
    );


    availableEndTimes = updateBooking ? [...availableEndTimesForUpdateBooking, ...config.custom.times] : [...availableEndTimes, ...config.custom.times];
    const filteredEndTimes = [];
    const filteredAvailableEndTimes = availableEndTimes.filter(a => {
      if (filteredEndTimes.indexOf(a.timeOfDay) === -1) {
        filteredEndTimes.push(a.timeOfDay);
        return a;
      }
    });

    filteredAvailableEndTimes.sort( (a, b) => Date.parse('01/01/2013 ' + a.timeOfDay) - Date.parse('01/01/2013 ' + b.timeOfDay));
    const endOptions = bookingEndDate
      ? filteredAvailableEndTimes.map(p => {
        const optionClass = p.className === 'unavailable' ? css.unavailable : css.available;
        const disabled = p.className === 'unavailable';
        return { value: p.timestamp, label: p.timeOfDay, className: optionClass, disabled };
      })
      : [];

    if (ifDropOffChanged) {
      availableEndTimes = getAvailableEndTimes(
        intl,
        timeZone,
        bookingStartTime,
        bookingEndDate,
        selectedTimeSlot,
        localTimeZone
      );
    }
    const isDayBlocked = timeSlotsOnSelectedMonth
      ? day =>
        !timeSlotsOnSelectedMonth.find(timeSlot =>
          isDayMomentInsideRange(
            day,
            timeSlot.attributes.start,
            timeSlot.attributes.end,
            timeZone
          )
        )
      : () => false;

    const placeholderTime = '00:00';
    const yesterday = Date.now() - 86400000;
    const twoHoursFromNow = TODAY.setHours(TODAY.getHours() + 2);
    const startDateDisabled = updateBooking && (new Date((parseInt(bookingStartDate) - 86400000)) < yesterday);
    const endDateDisabled = !bookingStartDate || (!updateBooking && !bookingStartTime) || updateBooking && parseInt(bookingEndTime) < twoHoursFromNow;
    const startTimeDisabled = !bookingStartDate || (new Date((parseInt(bookingStartDate) - 86400000)) < yesterday);
    const endTimeDisabled = !bookingEndDate || (updateBooking && parseInt(bookingEndTime) < twoHoursFromNow);

    const startTimeLabel = intl.formatMessage({ id: 'FieldDateTimeInput.startTime' });
    const endTimeLabel = intl.formatMessage({ id: 'FieldDateTimeInput.endTime' });
    /**
     * NOTE: In this template the field for the end date is hidden by default.
     * If you want to enable longer booking periods, showing the end date in the form requires some code changes:
     * 1. Move the bookingStartTime field to the same formRow with the bookingStartDate field
     * 2. Remove the div containing the line between dates
     * 3. Remove the css related to hiding the booking end date from the bottom of the FieldDateAndTimeInput.css field
     */

    return (
      <div className={classes}>
        <div className={classNames(css.formRow, css.startDate)}>
          <div className={classNames(css.field)}>
            <FieldDateInput
              labelClassName={css.label}
              className={css.fieldDateInput}
              name="bookingStartDate"
              id={formId ? `${formId}.bookingStartDate` : 'bookingStartDate'}
              label={startDateInputProps.label}
              placeholderText={startDateInputProps.placeholderText}
              format={v =>
                v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : v
              }
              parse={v =>
                v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v
              }
              isDayBlocked={isDayBlocked}
              disabled={startDateDisabled}
              onChange={this.onBookingStartDateChange}
              onPrevMonthClick={() => this.onMonthClick(prevMonthFn)}
              onNextMonthClick={() => this.onMonthClick(nextMonthFn)}
              navNext={<Next currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              navPrev={<Prev localTimeZone={localTimeZone} currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              useMobileMargins
              showErrorMessage={false}
              validate={bookingDateRequired('Required')}
              // isOutsideRange={day => this.startDateIsOutsizeRange(day, timeZone)}
            />
          </div>
          <div className={bookingStartDate ? css.lineBetween : css.lineBetweenDisabled}>-</div>
          <div className={css.field}>
            <FieldSelect
              labelClassName={css.label}
              name="bookingStartTime"
              id={formId ? `${formId}.bookingStartTime` : 'bookingStartTime'}
              className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
              selectClassName={bookingStartDate ? css.select : css.selectDisabled}
              label={startTimeLabel}
              disabled={startTimeDisabled}
              onChange={this.onBookingStartTimeChange}
            >
              <option value="">{initialValues.bookingStartDate && initialValues.bookingStartDate.date ? moment(initialValues.bookingStartDate.date).format('hh:mm a') : '00:00'}</option>
              {bookingStartDate ? (
                startOptions.map(p => {
                  return <option disabled={p.disabled}
                                 className={p.className}
                                 key={`bookingStartTime_${p.value}`}
                                 value={p.value}>
                    {p.label}
                  </option>
                })
              ) : (
                <option>{placeholderTime}</option>
              )}
            </FieldSelect>
          </div>
        </div>
        <div className={classNames(css.formRow, css.endDate)}>
          <div className={classNames(css.field)}>
            <FieldDateInput
              {...endDateInputProps}
              labelClassName={css.label}
              name="bookingEndDate"
              id={formId ? `${formId}.bookingEndDate` : 'bookingEndDate'}
              className={css.fieldDateInput}
              label={endDateInputProps.label}
              placeholderText={endDateInputProps.placeholderText}
              format={v =>
                v && v.date ? { date: timeOfDayFromTimeZoneToLocal(v.date, timeZone) } : v
              }
              parse={v =>
                v && v.date ? { date: timeOfDayFromLocalToTimeZone(v.date, timeZone) } : v
              }
              isDayBlocked={isDayBlocked}
              onChange={this.onBookingEndDateChange}
              onPrevMonthClick={() => this.onMonthClick(prevMonthFn)}
              onNextMonthClick={() => this.onMonthClick(nextMonthFn)}
              navNext={<Next currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              navPrev={<Prev localTimeZone={localTimeZone} currentMonth={this.state.currentMonth} timeZone={timeZone} />}
              isOutsideRange={day =>
                this.isOutsideRange(
                  day,
                  bookingStartDate,
                  selectedTimeSlot,
                  timeZone
                )
              }
              useMobileMargins
              showErrorMessage={false}
              disabled={endDateDisabled}
              showLabelAsDisabled={endDateDisabled}
              validate={bookingDateRequired('Required')}
            />
          </div>

          <div className={bookingStartDate ? css.lineBetween : css.lineBetweenDisabled}>-</div>

          <div className={css.field}>
            <FieldSelect
              labelClassName={css.label}
              name="bookingEndTime"
              id={formId ? `${formId}.bookingEndTime` : 'bookingEndTime'}
              className={bookingStartDate ? css.fieldSelect : css.fieldSelectDisabled}
              selectClassName={bookingStartDate ? css.select : css.selectDisabled}
              label={endTimeLabel}
              disabled={endTimeDisabled}
            >
              {bookingEndDate ? (
                <Fragment>
                  <option value="">{initialValues.bookingEndDate && initialValues.bookingEndDate.date ? moment(initialValues.bookingEndDate.date).format('hh:mm a') : '00:00'}</option>
                  {endOptions.map(p => (
                    <option
                      disabled={p.disabled}
                      className={p.className}
                      key={`bookingEndTime_${p.value}`}
                      value={p.value}
                    >
                      {p.label}
                    </option>
                  ))}
                </Fragment>
              ) : (
                <option>{placeholderTime}</option>
              )}
            </FieldSelect>
          </div>
        </div>
      </div>
    );
  }
}

FieldDateAndTimeInput.defaultProps = {
  rootClassName: null,
  className: null,
  startDateInputProps: null,
  endDateInputProps: null,
  startTimeInputProps: null,
  endTimeInputProps: null,
  listingId: null,
  timeZone: null,
  isOutsideRange: () => false,
};

FieldDateAndTimeInput.propTypes = {
  rootClassName: string,
  className: string,
  formId: string,
  bookingStartLabel: string,
  startDateInputProps: object,
  endDateInputProps: object,
  startTimeInputProps: object,
  endTimeInputProps: object,
  form: object.isRequired,
  values: object.isRequired,
  listingId: propTypes.uuid,
  onFetchTimeSlots: func.isRequired,
  timeZone: string,
  isOutsideRange: func,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default FieldDateAndTimeInput;

